<template>
  <div class="min-h-screen flex bg-background">

    <!-- ── Left branding panel ─────────────────────────────────── -->
    <div class="hidden lg:flex lg:w-[440px] xl:w-[480px] shrink-0 flex-col bg-sidebar text-sidebar-foreground">

      <!-- Logo -->
      <div class="flex items-center gap-3 px-8 pt-8 pb-10">
        <img :src="logoMark" alt="ExoTrack" class="h-9 w-9 shrink-0" />
        <div>
          <p class="text-base font-bold font-display text-sidebar-foreground leading-none">ExoTrack</p>
          <p class="text-xs text-sidebar-muted-foreground mt-0.5">HR & Payroll by Exodus Software</p>
        </div>
      </div>

      <!-- Feature highlights -->
      <div class="flex-1 px-8 py-4 space-y-6">
        <div>
          <h2 class="text-2xl font-bold text-sidebar-foreground leading-snug">
            {{ t('auth.tagline') }}
          </h2>
          <p class="mt-3 text-sm text-sidebar-muted-foreground leading-relaxed">
            {{ t('auth.taglineDesc') }}
          </p>
        </div>

        <div class="space-y-4 pt-2">
          <div v-for="feat in features" :key="feat.label" class="flex items-start gap-3">
            <div class="h-7 w-7 rounded-md bg-sidebar-accent flex items-center justify-center shrink-0 mt-0.5">
              <component :is="feat.icon" class="h-3.5 w-3.5 text-primary" />
            </div>
            <div>
              <p class="text-sm font-medium text-sidebar-foreground">{{ feat.label }}</p>
              <p class="text-xs text-sidebar-muted-foreground mt-0.5 leading-relaxed">{{ feat.desc }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Bottom status -->
      <div class="px-8 py-6 border-t border-sidebar-border">
        <div class="flex items-center justify-between">
          <p class="text-xs text-sidebar-muted-foreground">© {{ new Date().getFullYear() }} ExoTrack</p>
          <div class="flex items-center gap-1.5">
            <div class="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
            <span class="text-xs text-sidebar-muted-foreground">{{ t('auth.allSystemsOperational') }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Right form panel ────────────────────────────────────── -->
    <div class="flex-1 flex flex-col items-center justify-center p-6 sm:p-10 overflow-y-auto">

      <!-- Mobile logo -->
      <div class="lg:hidden mb-8">
        <img :src="logoLockup" alt="ExoTrack" class="h-14" />
      </div>

      <div class="w-full max-w-sm">

        <!-- Language toggle -->
        <div class="flex justify-end mb-4">
          <LangToggle />
        </div>

        <!-- Heading -->
        <div class="mb-6">
          <h1 class="text-2xl font-bold text-foreground">{{ t('auth.signIn') }}</h1>
          <p class="mt-1 text-sm text-muted-foreground">
            {{ loginType === 'employee' ? t('auth.employeeSubtitle') : t('auth.adminSubtitle') }}
          </p>
        </div>

        <!-- Role toggle -->
        <div class="flex rounded-md border border-border bg-muted p-0.5 mb-6">
          <button
            type="button"
            :class="[
              'flex-1 flex items-center justify-center gap-1.5 py-2 text-sm font-medium rounded transition-all duration-150',
              loginType === 'employee'
                ? 'bg-white text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            ]"
            :disabled="authStore.loading"
            @click="loginType = 'employee'"
          >
            <Users class="h-3.5 w-3.5" />
            {{ t('auth.employee') }}
          </button>
          <button
            type="button"
            :class="[
              'flex-1 flex items-center justify-center gap-1.5 py-2 text-sm font-medium rounded transition-all duration-150',
              loginType === 'admin'
                ? 'bg-white text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            ]"
            :disabled="authStore.loading"
            @click="loginType = 'admin'"
          >
            <Shield class="h-3.5 w-3.5" />
            {{ t('auth.admin') }}
          </button>
        </div>

        <!-- Error -->
        <div
          v-if="authStore.error || localError"
          class="flex items-start gap-2.5 mb-4 p-3 rounded-md bg-destructive/10 border border-destructive/20 text-destructive text-sm"
        >
          <AlertCircle class="h-4 w-4 shrink-0 mt-0.5" />
          <span class="flex-1">{{ authStore.error || localError }}</span>
          <button @click="clearErrors" class="text-destructive/70 hover:text-destructive transition-colors">
            <X class="h-4 w-4" />
          </button>
        </div>

        <!-- Success -->
        <div
          v-if="successMessage"
          class="flex items-center gap-2.5 mb-4 p-3 rounded-md bg-success/10 border border-success/20 text-success text-sm"
        >
          <CheckCircle class="h-4 w-4 shrink-0" />
          {{ successMessage }}
        </div>

        <!-- Form -->
        <form @submit.prevent="handleLogin" class="space-y-4">

          <!-- Email -->
          <div class="space-y-1.5">
            <label class="text-sm font-medium text-foreground">{{ t('auth.emailAddress') }}</label>
            <div class="relative">
              <Mail class="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <input
                v-model="credentials.email"
                type="email"
                placeholder="you@company.com"
                required
                autocomplete="email"
                :disabled="authStore.loading"
                class="w-full pl-9 pr-3 py-2 text-sm border rounded-md bg-background text-foreground placeholder:text-muted-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                :class="errors.email ? 'border-destructive' : 'border-border'"
                @input="clearFieldError('email')"
              />
            </div>
            <p v-if="errors.email" class="text-xs text-destructive">{{ errors.email }}</p>
          </div>

          <!-- Password -->
          <div class="space-y-1.5">
            <div class="flex items-center justify-between">
              <label class="text-sm font-medium text-foreground">{{ t('auth.password') }}</label>
              <button
                type="button"
                class="text-xs text-primary hover:text-primary/80 transition-colors"
                @click="showForgotPassword = true"
              >
                {{ t('auth.forgotPassword') }}
              </button>
            </div>
            <div class="relative">
              <Lock class="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <input
                v-model="credentials.password"
                :type="showPassword ? 'text' : 'password'"
                :placeholder="t('auth.enterPassword')"
                required
                autocomplete="current-password"
                :disabled="authStore.loading"
                class="w-full pl-9 pr-9 py-2 text-sm border rounded-md bg-background text-foreground placeholder:text-muted-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                :class="errors.password ? 'border-destructive' : 'border-border'"
                @input="clearFieldError('password')"
              />
              <button
                type="button"
                class="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                :disabled="authStore.loading"
                @click="showPassword = !showPassword"
              >
                <Eye v-if="showPassword" class="h-4 w-4" />
                <EyeOff v-else class="h-4 w-4" />
              </button>
            </div>
            <p v-if="errors.password" class="text-xs text-destructive">{{ errors.password }}</p>
          </div>

          <!-- Remember me -->
          <label class="flex items-center gap-2 cursor-pointer select-none">
            <input
              v-model="credentials.rememberMe"
              type="checkbox"
              class="h-4 w-4 rounded border-border text-primary focus:ring-ring focus:ring-2 focus:ring-offset-0 transition-colors"
              :disabled="authStore.loading"
            />
            <span class="text-sm text-muted-foreground">{{ t('auth.rememberMe') }}</span>
          </label>

          <!-- Submit -->
          <button
            type="submit"
            :disabled="authStore.loading || !isFormValid"
            class="w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-primary text-primary-foreground text-sm font-semibold rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors mt-2"
          >
            <Loader2 v-if="authStore.loading" class="h-4 w-4 animate-spin" />
            <LogIn v-else class="h-4 w-4" />
            {{ authStore.loading ? t('auth.signingIn') : t('auth.signIn') }}
          </button>

        </form>

        <!-- Android app download -->
        <div class="flex justify-center mt-6">
          <a href="/downloads/ExoTrack.apk" download="ExoTrack.apk"
             class="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors">
            <Smartphone class="h-4 w-4" />
            {{ t('auth.getAndroidApp') }}
          </a>
        </div>

        <!-- Trust badges -->
        <div class="flex items-center justify-center gap-4 mt-8 pt-6 border-t border-border">
          <div class="flex items-center gap-1.5 text-xs text-muted-foreground">
            <div class="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            {{ t('auth.secure') }}
          </div>
          <div class="flex items-center gap-1.5 text-xs text-muted-foreground">
            <div class="h-1.5 w-1.5 rounded-full bg-blue-500" />
            {{ t('auth.encrypted') }}
          </div>
          <div class="flex items-center gap-1.5 text-xs text-muted-foreground">
            <div class="h-1.5 w-1.5 rounded-full bg-violet-500" />
            {{ t('auth.protected') }}
          </div>
        </div>

      </div>
    </div>

  </div>

  <!-- ── Forgot password modal ───────────────────────────────── -->
  <Transition name="fade">
    <div
      v-if="showForgotPassword"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      @click.self="showForgotPassword = false"
    >
      <div class="w-full max-w-sm bg-white rounded-lg border border-border shadow-lg p-6">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-base font-semibold text-foreground">{{ t('auth.resetPassword') }}</h3>
          <button
            class="text-muted-foreground hover:text-foreground transition-colors"
            @click="showForgotPassword = false"
          >
            <X class="h-4 w-4" />
          </button>
        </div>
        <p class="text-sm text-muted-foreground mb-4">
          {{ t('auth.resetInstructions') }}
        </p>
        <form @submit.prevent="handleForgotPassword" class="space-y-4">
          <div class="relative">
            <Mail class="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <input
              v-model="forgotPasswordEmail"
              type="email"
              placeholder="you@company.com"
              required
              class="w-full pl-9 pr-3 py-2 text-sm border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
            />
          </div>
          <div class="flex gap-2">
            <button
              type="button"
              class="flex-1 py-2 px-3 text-sm font-medium rounded-md border border-border text-foreground hover:bg-muted transition-colors"
              @click="showForgotPassword = false"
            >
              {{ t('common.cancel') }}
            </button>
            <button
              type="submit"
              class="flex-1 py-2 px-3 text-sm font-semibold rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              {{ t('auth.sendLink') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import {
  Shield, Users, Mail, Lock, Eye, EyeOff, LogIn, Loader2,
  AlertCircle, CheckCircle, X, Clock, CalendarCheck, BarChart3, Banknote,
  Smartphone,
} from 'lucide-vue-next'
import LangToggle from '@/components/common/LangToggle.vue'
import logoMark from '@/assets/brand/logo-mark.svg'
import logoLockup from '@/assets/brand/logo.svg'

const { t } = useI18n()

const router    = useRouter()
const authStore = useAuthStore()

const isDemoMode = computed(() => import.meta.env.VITE_ENABLE_DEMO_MODE === 'true')

const loginType          = ref('employee')
const showPassword       = ref(false)
const showForgotPassword = ref(false)
const localError         = ref('')
const successMessage     = ref('')
const forgotPasswordEmail = ref('')

const credentials = ref({ email: '', password: '', rememberMe: false })
const errors      = ref({ email: '', password: '' })

const features = computed(() => [
  { icon: Clock,         label: t('auth.featAttendance'),  desc: t('auth.featAttendanceDesc') },
  { icon: CalendarCheck, label: t('auth.featLeave'),       desc: t('auth.featLeaveDesc') },
  { icon: BarChart3,     label: t('auth.featReports'),     desc: t('auth.featReportsDesc') },
  { icon: Banknote,      label: t('auth.featPayroll'),     desc: t('auth.featPayrollDesc') },
])

const isFormValid = computed(() =>
  credentials.value.email && credentials.value.password &&
  !errors.value.email && !errors.value.password
)

const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

const validateForm = () => {
  errors.value = { email: '', password: '' }
  let ok = true

  if (!credentials.value.email) {
    errors.value.email = t('auth.emailRequired'); ok = false
  } else if (!validateEmail(credentials.value.email)) {
    errors.value.email = t('common.invalidEmail'); ok = false
  }

  if (!credentials.value.password) {
    errors.value.password = t('auth.passwordRequired'); ok = false
  } else if (credentials.value.password.length < 3) {
    errors.value.password = t('auth.passwordMinLength'); ok = false
  }

  return ok
}

const clearFieldError = (field) => { errors.value[field] = ''; localError.value = '' }

const clearErrors = () => {
  authStore.error = null
  localError.value = ''
  errors.value = { email: '', password: '' }
}

const handleLogin = async () => {
  clearErrors()
  if (!validateForm()) return

  try {
    const result = loginType.value === 'admin'
      ? await authStore.loginAdmin(credentials.value)
      : await authStore.loginEmployee(credentials.value)

    if (result.success) {
      successMessage.value = 'Login successful! Redirecting…'
      credentials.value = { email: '', password: '', rememberMe: false }
      setTimeout(() => {
        router.push(loginType.value === 'admin' ? '/admin/dashboard' : '/employee/dashboard')
      }, 800)
    } else {
      localError.value = result.error || t('auth.loginFailed')
    }
  } catch {
    localError.value = t('auth.unexpectedError')
  }
}

const handleForgotPassword = async () => {
  if (!forgotPasswordEmail.value) return
  showForgotPassword.value = false
  forgotPasswordEmail.value = ''
  successMessage.value = 'Password reset link sent to your email!'
  setTimeout(() => { successMessage.value = '' }, 5000)
}

watch(() => loginType.value, () => {
  clearErrors()
  credentials.value = { email: '', password: '', rememberMe: false }
})
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
